import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { LabelValue } from "@/Component/Value";
import { useIncidentReportLookupsQuery, useIncidentReportsReportJson, useIncidentReportsReportMutation } from "@/services/reports";
import { cn } from "@/utils/helpers";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";

const IncidentAndReportLogs = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const [page, setPage] = useState(1);

  const { data: lookupsRes } = useIncidentReportLookupsQuery({ system: "makkah" });
  const lookups = lookupsRes?.data?.data ?? lookupsRes?.data ?? {};

  const toSelectOptions = (items) =>
    (items || []).map((item) => ({
      value: item?.id != null ? Number(item.id) : item.id,
      label: getLocalizedText(item?.name_ar, item?.name_en) ?? item?.name_en ?? item?.name_ar ?? "",
    }));

  const caseTypesOptions = useMemo(() => toSelectOptions(lookups?.case_types?.items ?? lookups?.case_types ?? []), [lookups?.case_types, getLocalizedText]);
  const caseAuthoritiesOptions = useMemo(
    () => toSelectOptions(lookups?.case_authorities?.items ?? lookups?.case_authorities ?? []),
    [lookups?.case_authorities, getLocalizedText]
  );
  const reportAuthoritiesOptions = useMemo(
    () => toSelectOptions(lookups?.report_authorities?.items ?? lookups?.report_authorities ?? []),
    [lookups?.report_authorities, getLocalizedText]
  );
  const bodyLocationsOptions = useMemo(
    () => toSelectOptions(lookups?.body_locations?.items ?? lookups?.body_locations ?? []),
    [lookups?.body_locations, getLocalizedText]
  );

  const { mutate: exportReport, isPending: isExporting } = useIncidentReportsReportMutation();

  const form = useForm({
    defaultValues: {
      format: "pdf",
      per_page: 15,
      report_limit: 10000,
    },
    disabled: isExporting,
  });

  const format = form.watch("format");
  const isJson = format === "json";

  const jsonParams = useMemo(() => {
    const values = form.getValues();
    return {
      ...values,
      format: "json",
      page,
      per_page: values?.per_page || 15,
    };
  }, [page, form]);

  const {
    data: jsonRes,
    isLoading: isLoadingJson,
    refetch,
    isRefetching,
  } = useIncidentReportsReportJson({
    params: jsonParams,
    enabled: false,
  });

  const rows = jsonRes?.data?.data ?? [];
  const meta = jsonRes?.data?.meta ?? {};

  useEffect(() => {
    if (isJson) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isJson]);

  const { control, formState: { errors } } = form;

  const columns = [
    { name: t("SERIAL"), key: "serial" },
    { name: t("PILGRIM_NAME"), key: "pilgrim_name" },
    { name: t("SERVICE_CENTER"), key: "service_center_name" },
    { name: t("CAMPAIGN_OR_GROUP_NUMBER"), key: "campaign_or_group_number" },
    { name: t("BORDER_NUMBER"), key: "border_number" },
    { name: t("PASSPORT_NUMBER"), key: "passport_number" },
    { name: t("INCIDENT_NUMBER"), key: "incident_number" },
    { name: t("REPORTED_AT"), key: "reported_at" },
    { name: t("RESOLVED_AT"), key: "resolved_at" },
  ];

  const ctrls = [
    {
      labelProps: { children: t("REPORT_FORMAT") },
      controllerProps: {
        name: "format",
        rules: { required: t("FIELD_REQUIRED") },
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="format"
            placeholder={t("REPORT_FORMAT")}
            status={error ? "error" : undefined}
            options={[
              { value: "json", label: t("VIEW_REPORTS") },
              { value: "pdf", label: t("EXPORT_PDF") },
              { value: "excel", label: t("EXPORT_EXCEL") },
            ]}
          />
        ),
      },
    },
    {
      labelProps: { children: t("CASE_TYPES") },
      controllerProps: {
        name: "case_type_id",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            showSearch
            optionFilterProp="label"
            id="case_type_id"
            placeholder={t("CASE_TYPES")}
            status={error ? "error" : undefined}
            options={caseTypesOptions}
          />
        ),
      },
    },
    {
      labelProps: { children: t("CASE_AUTHORITY") },
      controllerProps: {
        name: "case_authority_id",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            showSearch
            optionFilterProp="label"
            id="case_authority_id"
            placeholder={t("CASE_AUTHORITY")}
            status={error ? "error" : undefined}
            options={caseAuthoritiesOptions}
          />
        ),
      },
    },
    {
      labelProps: { children: t("REPORT_AUTHORITY") },
      controllerProps: {
        name: "report_authority_id",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            showSearch
            optionFilterProp="label"
            id="report_authority_id"
            placeholder={t("REPORT_AUTHORITY")}
            status={error ? "error" : undefined}
            options={reportAuthoritiesOptions}
          />
        ),
      },
    },
    {
      labelProps: { children: t("BODY_LOCATION") },
      controllerProps: {
        name: "body_location_id",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            showSearch
            optionFilterProp="label"
            id="body_location_id"
            placeholder={t("BODY_LOCATION")}
            status={error ? "error" : undefined}
            options={bodyLocationsOptions}
          />
        ),
      },
    },
    {
      labelProps: { children: t("SERVICE_CENTER_ID") },
      controllerProps: {
        name: "service_center_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            id="service_center_id"
            showSearch
            placeholder={t("CAMPAIGNS_FORM_SERVICE_CENTER_PLACEHOLDER")}
            optionKey="service_centers"
            options={(serviceCenter) => ({
              value: String(serviceCenter.id),
              label: serviceCenter.name,
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("NATIONALITY") },
      controllerProps: {
        name: "nationality_id",
        render: ({ field }) => (
          <SelectCountry
            {...field}
            id="nationality_id"
            placeholder={t("NATIONALITY")}
          />
        ),
      },
    },
    {
      labelProps: { children: t("BORDER_NUMBER") },
      controllerProps: {
        name: "border_number",
        render: ({ field, fieldState: { error } }) => (
          <AntInput {...field} id="border_number" placeholder={t("BORDER_NUMBER")} status={error ? "error" : undefined} />
        ),
      },
    },
    {
      labelProps: { children: t("CAMPAIGN_NUMBER") },
      controllerProps: {
        name: "campaign_number",
        render: ({ field, fieldState: { error } }) => (
          <AntInput {...field} id="campaign_number" placeholder={t("CAMPAIGN_NUMBER")} status={error ? "error" : undefined} />
        ),
      },
    },
    {
      labelProps: { children: t("GROUP_NUMBER") },
      controllerProps: {
        name: "group_number",
        render: ({ field, fieldState: { error } }) => (
          <AntInput {...field} id="group_number" placeholder={t("GROUP_NUMBER")} status={error ? "error" : undefined} />
        ),
      },
    },
    {
      labelProps: { children: t("REPORTED_FROM") },
      controllerProps: {
        name: "reported_from",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            size="large"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) => field.onChange(date ? date.format("YYYY-MM-DD") : null)}
            id="reported_from"
            className="w-100"
            placeholder={t("REPORTED_FROM")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
          />
        ),
      },
    },
    {
      labelProps: { children: t("REPORTED_TO") },
      controllerProps: {
        name: "reported_to",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            size="large"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) => field.onChange(date ? date.format("YYYY-MM-DD") : null)}
            id="reported_to"
            className="w-100"
            placeholder={t("REPORTED_TO")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
          />
        ),
      },
    },
    {
      labelProps: { children: t("CASE_STATUS") },
      controllerProps: {
        name: "is_resolved",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="is_resolved"
            placeholder={t("CASE_STATUS")}
            status={error ? "error" : undefined}
            options={[
              { value: 1, label: t("CASE_COMPLETED") },
              { value: 0, label: t("UNRESOLVED") },
            ]}
          />
        ),
      },
    },
  ];

  const onSubmit = (data) => {
    if (data.format === "json") {
      setPage(1);
      return refetch();
    }
    exportReport({ params: data });
  };

  const isLoading = isExporting || isLoadingJson;
  const backTo = window.history.length > 1 ? -1 : `/app/makkah`;

  return (
    <div>
      <PageHeader title={t("INCIDENT_AND_REPORT_LOGS")} sticky={false} />
      <Container fluid>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
          <Controllers
            watch={form.watch}
            {...{
              controllers: ctrls.map((config) => ({
                ...config,
                labelProps: {
                  ...config.labelProps,
                  className: cn("fw-bold", config.labelProps.className),
                },
                controllerProps: {
                  ...config.controllerProps,
                  control,
                  errors,
                },
                layout: {
                  colProps: { xs: 6, md: 6 },
                },
              })),
            }}
          />
          <ActionsFooter>
            <Button disabled={isLoading} className="btn btn-primary" type="submit">
              {isLoading ? <Spinner size="sm" /> : isJson ? t("VIEW_REPORTS") : format === "excel" ? t("EXPORT_EXCEL") : t("EXPORT_PDF")}
            </Button>
            <Link
              to={backTo}
              disabled={isLoading}
              style={{ pointerEvents: isLoading ? "none" : "auto" }}
              className="btn btn-danger"
              type="button"
            >
              {t("CANCEL")}
            </Link>
            <button onClick={() => form.reset()} disabled={isLoading} className="btn btn-outline-primary btn-sm" type="button">
              {t("RESET")}
            </button>
          </ActionsFooter>
        </form>

        {isJson && (
          <TableC
            columnsFilter={false}
            data={rows}
            columns={columns}
            loading={isLoadingJson || isRefetching}
            skeletonCount={10}
            pagination={{
              current: page,
              total: meta?.total || 0,
              pageSize: meta?.per_page || 15,
              onChange: (p) => setPage(p),
            }}
          />
        )}
      </Container>
    </div>
  );
};

export default IncidentAndReportLogs;
